/*
 * American Fur — dedicated product page template behavior.
 * Loaded with `defer` only on templates/product.american-fur.json (see theme.liquid override).
 * Depends on global.js already being loaded first for: trapFocus, <quantity-input>,
 * <modal-dialog>, <modal-opener> (all defined theme-wide in assets/global.js).
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && window.money_format) {
      return Shopify.formatMoney(cents, window.money_format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* ---------------- Sticky purchase-actions offset ---------------- */
  function initStickyOffset(root) {
    var pageWrapper = document.querySelector('.page-wrapper');
    /* Fallback for browsers without :has() support — see the matching rule in
       component-product-american-fur.css for why this override is needed at all. */
    if (pageWrapper && !CSS.supports('selector(:has(*))')) {
      pageWrapper.style.overflow = 'visible';
    }

    var header = document.getElementById('af-header');
    if (!header) return;

    var ticking = false;

    function updateOffset() {
      ticking = false;
      var height = header.getBoundingClientRect().height;
      root.style.setProperty('--af-pdp-sticky-top', Math.round(height + 16) + 'px');
    }

    function requestUpdate() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateOffset);
    }

    updateOffset();
    window.addEventListener('resize', requestUpdate);
    window.addEventListener('scroll', requestUpdate, { passive: true });
  }

  /* ---------------- Gallery: thumbnails, zoom pane, lightbox ---------------- */
  /* ---------------- Thumbnail rail: scroll + arrow stepping ---------------- */
  function initThumbRail(gallery) {
    var rail = gallery.querySelector('[data-pdp-thumb-rail]');
    var track = gallery.querySelector('[data-pdp-thumb-track]');
    var prev = rail && rail.querySelector('[data-pdp-thumb-prev]');
    var next = rail && rail.querySelector('[data-pdp-thumb-next]');
    if (!rail || !track || !prev || !next) return null;

    // Column on desktop/tablet, row on mobile -- read the axis rather than the width.
    function vertical() {
      return getComputedStyle(track).flexDirection.indexOf('column') === 0;
    }

    function metrics() {
      var v = vertical();
      return {
        vertical: v,
        pos: v ? track.scrollTop : track.scrollLeft,
        size: v ? track.clientHeight : track.clientWidth,
        total: v ? track.scrollHeight : track.scrollWidth
      };
    }

    function scrollTo(pos, m) {
      if (m.vertical) track.scrollTop = pos;
      else track.scrollLeft = pos;
    }

    function update() {
      var m = metrics();
      var overflows = m.total - m.size > 1;
      prev.hidden = !overflows;
      next.hidden = !overflows;
      if (!overflows) return;
      prev.disabled = m.pos <= 1;
      next.disabled = m.pos >= m.total - m.size - 1;
    }

    function step(direction) {
      var m = metrics();
      var item = track.querySelector('li');
      var rect = item && item.getBoundingClientRect();
      var itemSize = rect ? (m.vertical ? rect.height : rect.width) + 11 : 93;
      // Advance a full page, less one thumb, so there is always visual overlap.
      var delta = Math.max(itemSize, m.size - itemSize) * direction;
      scrollTo(m.pos + delta, m);
    }

    // Used when the variant picker activates a thumb that is scrolled out of view.
    function reveal(thumb) {
      if (!thumb || !thumb.parentNode) return;
      var m = metrics();
      if (m.total - m.size <= 1) return;

      var trackRect = track.getBoundingClientRect();
      var itemRect = thumb.parentNode.getBoundingClientRect();
      var offset = m.vertical ? itemRect.top - trackRect.top : itemRect.left - trackRect.left;
      var itemSize = m.vertical ? itemRect.height : itemRect.width;

      if (offset < 0) scrollTo(m.pos + offset, m);
      else if (offset + itemSize > m.size) scrollTo(m.pos + offset + itemSize - m.size, m);
    }

    prev.addEventListener('click', function () { step(-1); });
    next.addEventListener('click', function () { step(1); });
    track.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    window.addEventListener('load', update);
    update();

    return { update: update, reveal: reveal };
  }

  function initGallery(root) {
    var gallery = root.querySelector('[data-pdp-gallery]');
    if (!gallery) return;

    var mainBtn = gallery.querySelector('[data-pdp-main]');
    var mainImg = gallery.querySelector('[data-pdp-main-image]');
    var zoomPane = gallery.querySelector('[data-pdp-zoom-pane]');
    var thumbs = gallery.querySelectorAll('[data-pdp-thumb]');
    var lightbox = document.getElementById('af-pdp-lightbox');
    var lightboxImage = lightbox ? lightbox.querySelector('[data-pdp-lightbox-image]') : null;
    var thumbRail = initThumbRail(gallery);

    function setActiveMedia(src, alt) {
      mainImg.setAttribute('src', src);
      mainImg.setAttribute('alt', alt || '');
      zoomPane.style.backgroundImage = 'url(' + src + ')';
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        thumbs.forEach(function (t) {
          t.classList.remove('is-active');
          t.setAttribute('aria-current', 'false');
        });
        thumb.classList.add('is-active');
        thumb.setAttribute('aria-current', 'true');
        setActiveMedia(thumb.getAttribute('data-full'), thumb.getAttribute('data-alt'));
        if (thumbRail) thumbRail.reveal(thumb);
      });
    });

    if (mainBtn) {
      mainBtn.addEventListener('pointerenter', function (event) {
        if (event.pointerType === 'touch') return;
        zoomPane.classList.add('is-active');
      });
      mainBtn.addEventListener('pointermove', function (event) {
        if (event.pointerType === 'touch') return;
        var rect = mainBtn.getBoundingClientRect();
        var x = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
        var y = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));
        zoomPane.style.backgroundPosition = x + '% ' + y + '%';
      });
      mainBtn.addEventListener('pointerleave', function () {
        zoomPane.classList.remove('is-active');
      });
      mainBtn.addEventListener('click', function () {
        if (!lightbox || !lightboxImage) return;
        lightboxImage.setAttribute('src', mainImg.getAttribute('src'));
        lightboxImage.setAttribute('alt', mainImg.getAttribute('alt'));
        lightbox.show(mainBtn);
      });
    }
  }

  /* ---------------- Variant selection ---------------- */
  function initVariants(root) {
    var form = root.querySelector('[data-pdp-form]');
    if (!form) return;

    var variantsScript = root.querySelector('[data-pdp-variants]');
    var variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      return;
    }

    var optionFieldsets = root.querySelectorAll('[data-pdp-option]');
    var idInput = form.querySelector('[data-pdp-variant-id]');
    var addBtn = root.querySelector('[data-pdp-add-btn]');
    var addBtnText = root.querySelector('[data-pdp-add-btn-text]');
    var stockStatus = root.querySelector('[data-pdp-stock-status]');
    var stockText = root.querySelector('[data-pdp-stock-text]');
    var priceCurrent = root.querySelector('[data-pdp-price-current]');
    var priceCompare = root.querySelector('[data-pdp-price-compare]');
    var priceSave = root.querySelector('[data-pdp-price-save]');
    var skuText = root.querySelector('[data-pdp-sku]');

    function currentSelections() {
      var selections = [];
      optionFieldsets.forEach(function (fieldset) {
        var active = fieldset.querySelector('[data-pdp-option-value-btn].is-selected');
        selections.push(active ? active.getAttribute('data-value') : null);
      });
      return selections;
    }

    function findMatch(selections) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var match = true;
        for (var j = 0; j < selections.length; j++) {
          if (v.options[j] !== selections[j]) { match = false; break; }
        }
        if (match) return v;
      }
      return null;
    }

    function refreshAvailability(selections) {
      optionFieldsets.forEach(function (fieldset, index) {
        var buttons = fieldset.querySelectorAll('[data-pdp-option-value-btn]');
        buttons.forEach(function (btn) {
          var testSelections = selections.slice();
          testSelections[index] = btn.getAttribute('data-value');
          var testVariant = findMatch(testSelections);
          var available = testVariant ? testVariant.available : true;
          btn.disabled = !available;
          btn.setAttribute('aria-disabled', available ? 'false' : 'true');
        });
      });
    }

    function updateForVariant(variant) {
      if (!variant) {
        addBtn.disabled = true;
        addBtn.setAttribute('aria-disabled', 'true');
        addBtnText.textContent = 'Unavailable';
        return;
      }

      idInput.value = variant.id;

      if (priceCurrent) priceCurrent.textContent = formatMoney(variant.price);
      if (priceCompare) {
        if (variant.compare_at_price > variant.price) {
          priceCompare.textContent = formatMoney(variant.compare_at_price);
          priceCompare.hidden = false;
        } else {
          priceCompare.hidden = true;
        }
      }
      if (priceSave) {
        if (variant.compare_at_price > variant.price) {
          var pct = Math.round((1 - (variant.price / variant.compare_at_price)) * 100);
          priceSave.textContent = 'Save ' + pct + '%';
          priceSave.hidden = false;
        } else {
          priceSave.hidden = true;
        }
      }
      if (skuText) skuText.textContent = 'SKU: ' + (variant.sku || '—');

      if (variant.available) {
        stockStatus.classList.remove('is-unavailable');
        stockText.textContent = 'In stock — ready to ship';
        addBtn.disabled = false;
        addBtn.removeAttribute('aria-disabled');
        addBtnText.textContent = 'Add to cart';
      } else {
        stockStatus.classList.add('is-unavailable');
        stockText.textContent = 'Out of stock';
        addBtn.disabled = true;
        addBtn.setAttribute('aria-disabled', 'true');
        addBtnText.textContent = 'Sold out';
      }

      if (variant.featured_image && variant.featured_image.src) {
        var thumb = root.querySelector('[data-pdp-thumb][data-full*="' + variant.featured_image.src.split('?')[0].split('/').pop() + '"]');
        if (thumb) thumb.click();
      }

      if (history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
    }

    optionFieldsets.forEach(function (fieldset) {
      var valueLabel = fieldset.querySelector('[data-pdp-option-value]');
      fieldset.querySelectorAll('[data-pdp-option-value-btn]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          fieldset.querySelectorAll('[data-pdp-option-value-btn]').forEach(function (b) {
            b.classList.remove('is-selected');
            b.setAttribute('aria-pressed', 'false');
          });
          btn.classList.add('is-selected');
          btn.setAttribute('aria-pressed', 'true');
          if (valueLabel) valueLabel.textContent = btn.getAttribute('data-value');

          var selections = currentSelections();
          refreshAvailability(selections);
          updateForVariant(findMatch(selections));
        });
      });
    });
  }

  /* ---------------- Add to cart ---------------- */
  function initAddToCart(root) {
    var form = root.querySelector('[data-pdp-form]');
    if (!form) return;

    var message = root.querySelector('[data-pdp-cart-message]');
    var redirecting = false;

    form.addEventListener('submit', function (event) {
      if (!window.routes || !window.routes.cart_add_url) return;
      event.preventDefault();
      if (redirecting) return;

      var submitBtn = form.querySelector('[data-pdp-add-btn]');
      var formData = new FormData(form);

      if (submitBtn) submitBtn.setAttribute('aria-disabled', 'true');
      if (message) {
        message.textContent = 'Adding to cart…';
        message.classList.remove('is-error');
      }

      fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      })
        .then(function (response) { return response.json().then(function (data) { return { ok: response.ok, data: data }; }); })
        .then(function (result) {
          if (!result.ok) {
            var description = (result.data && (result.data.description || result.data.message)) || 'This item could not be added to your cart.';
            if (message) {
              message.textContent = description;
              message.classList.add('is-error');
            }
            return;
          }

          if (message) {
            var qty = result.data.quantity || 1;
            message.textContent = qty + ' × ' + result.data.product_title + ' added to your cart. Taking you to your cart…';
          }

          // Successful add: send the shopper to the cart page.
          redirecting = true;
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        })
        .catch(function () {
          if (message) {
            message.textContent = 'Something went wrong. Please try again.';
            message.classList.add('is-error');
          }
        })
        .finally(function () {
          // Leave the button disabled while the cart page is loading.
          if (submitBtn && !redirecting) submitBtn.removeAttribute('aria-disabled');
        });
    });
  }

  /* ---------------- Related products (Shopify recommendations) ---------------- */
  function initRecommendations() {
    if (customElements.get('product-recommendations')) return;

    customElements.define('product-recommendations', class extends HTMLElement {
      connectedCallback() {
        var url = this.dataset.url;
        if (!url) return;
        var self = this;

        var wrap = self.closest('[data-pdp-related]');

        fetch(url)
          .then(function (response) { return response.text(); })
          .then(function (text) {
            var html = document.createElement('div');
            html.innerHTML = text;
            var recommendations = html.querySelector('product-recommendations');
            if (recommendations && recommendations.innerHTML.trim().length) {
              self.innerHTML = recommendations.innerHTML;
              if (wrap) wrap.removeAttribute('hidden');
            } else if (wrap) {
              wrap.setAttribute('hidden', '');
            }
          })
          .catch(function () {
            if (wrap) wrap.setAttribute('hidden', '');
          });
      }
    });
  }

  /* ---------------- Recently viewed (localStorage, no personal data) ---------------- */
  function initRecentlyViewed(root) {
    var STORAGE_KEY = 'af_pdp_recently_viewed';
    var currentScript = root.querySelector('[data-pdp-current-product]');
    var container = root.querySelector('[data-pdp-recent]');
    if (!currentScript || !container) return;

    var current;
    try {
      current = JSON.parse(currentScript.textContent);
    } catch (e) {
      return;
    }

    var stored = [];
    try {
      stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      stored = [];
    }

    var next = stored.filter(function (item) { return item.handle !== current.handle; });
    next.unshift(current);
    next = next.slice(0, 6);

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) { /* storage unavailable — skip persistence */ }

    var match = stored.find(function (item) { return item.handle !== current.handle; });
    if (!match) return;

    var link = container.querySelector('[data-pdp-recent-link]');
    var img = container.querySelector('[data-pdp-recent-image]');
    var title = container.querySelector('[data-pdp-recent-title]');
    var price = container.querySelector('[data-pdp-recent-price]');
    var compare = container.querySelector('[data-pdp-recent-compare]');

    link.setAttribute('href', match.url);
    img.setAttribute('src', match.image);
    img.setAttribute('alt', match.title);
    title.textContent = match.title;
    price.textContent = match.price;
    if (match.compareAtPrice) {
      compare.textContent = match.compareAtPrice;
      compare.hidden = false;
    }

    container.removeAttribute('hidden');
  }

  document.addEventListener('DOMContentLoaded', function () {
    var root = document.querySelector('.af-pdp');
    if (!root) return;

    initStickyOffset(root);
    initGallery(root);
    initVariants(root);
    initAddToCart(root);
    initRecommendations();
    initRecentlyViewed(root);
  });
})();
